// src/pages/CharacterPage.tsx - FigryneのUserProfilePageレイアウトベース
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getCharacter, getPublicCharacter, toggleCharacterFollow, toggleCharacterLike, isFollowingCharacter, hasLikedCharacter, updateCharacterHeaderImage } from '../services/characterService';
import { Character } from '../types/character';
import { PhotoIcon, HeartIcon, PencilIcon, CameraIcon, UserIcon, PaintBrushIcon, LightBulbIcon, GiftIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon, ChevronLeftIcon, PencilIcon as PencilSolidIcon } from '@heroicons/react/24/solid';
import HeaderImageCropper from '../components/character/HeaderImageCropper';
import XorPixViewer from '../components/xorpix/XorPixViewer';
import { ThemeToggle } from '../components/ui/ThemeToggle';
import CharacterEditModal from '../components/character/CharacterEditModal';
import CharacterShareModal from '../components/character/CharacterShareModal';
import CharacterSettingsModal from '../components/character/CharacterSettingsModal';
import ARViewerSection from '../components/character/ARViewerSection';
import { CharacterGalleryGrid, CharacterGalleryUpload, CharacterFanartGrid, FanartUploadModal, PhotoModal, GalleryImage, FanartImage } from '../components/gallery';
import CardViewerModal from '../components/gallery/CardViewerModal';
import { ReferenceGallery } from '../components/reference';
import { ShareIcon, LinkIcon } from '@heroicons/react/24/outline';
import { CHARACTER_LINK_PLATFORMS, CharacterLinkPlatform, MAX_CHARACTER_LINKS } from '../constants/characterLinks';
import { CharacterLink } from '../types/character';
import { updateCharacter as updateCharacterData } from '../services/characterService';
import { sanitizeInput } from '../utils/inputSanitizer';
import { config } from '../config';
import { orifoliCache } from '../services/orifoliCache';
import LoginModal from '../components/auth/LoginModal';
import FigryneGiftModal from '../components/character/FigryneGiftModal';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAvatarCache } from '../hooks/useAvatarCache';
import logoWhite from '../assets/images/UI_orifoli_logo_white.png';
import Toast, { showToast } from '../components/common/Toast';

// 画面サイズ判定フック（XorPixViewerの重複レンダリング防止）
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

// Character Card component for profile display
interface CharacterCardProps {
  character: Character;
  size?: 'small' | 'large';
  dimText?: boolean; // Reduce text opacity (for desktop where full text is shown below)
  isOwner?: boolean; // Show public status badge for owner
  onEdit?: () => void; // Edit button callback
  editHint?: { show: boolean; message: string; onDismiss: () => void };
}

const CharacterCard: React.FC<CharacterCardProps> = ({ character, size = 'large', dimText = false, isOwner = false, onEdit, editHint }) => {
  const [imageError, setImageError] = useState(false);
  const { t } = useTranslation('character');
  const { t: tTop } = useTranslation('top');
  const cardTheme = character.cardTheme || 'dark';
  const cachedAvatarSrc = useAvatarCache(character.ownerAvatar);
  const [avatarLoaded, setAvatarLoaded] = useState(false);
  // XOR-Pix対応: noise1, noise2, ridが揃っている場合はXorPixViewerを使用
  const hasXorPix = character.noise1Url && character.noise2Url && character.rid;
  const fallbackImageUrl = character.thumbnailUrl || character.avatarUrl || character.headerImageUrl;

  const displayName = character.ownerName || character.ownerUsername || 'Creator';
  const username = character.ownerUsername || '';

  const isSmall = size === 'small';

  // Hide text when dimText mode is active (desktop shows full text below card)
  const getTextClass = (normalClass: string, _dimClass: string) => {
    return dimText ? 'opacity-0' : normalClass;
  };

  return (
    <div className={`relative bg-white p-2 rounded-2xl shadow-lg ${isSmall ? 'w-32' : 'w-full max-w-[320px]'}`}>
      <div className="relative aspect-[2/3] rounded-xl overflow-hidden">
        {/* Full Bleed Image - XOR-Pix対応 */}
        {hasXorPix && !imageError ? (
          <XorPixViewer
            noise1Url={character.noise1Url!}
            noise2Url={character.noise2Url!}
            rid={character.rid!}
            width={character.imageWidth || 800}
            height={character.imageHeight || 1200}
            characterId={character.id}
            fallbackNoise1Url={character.fallbackNoise1Url}
            fallbackNoise2Url={character.fallbackNoise2Url}
            fitMode="cover"
            className="absolute inset-0 w-full h-full"
            onError={() => setImageError(true)}
          />
        ) : fallbackImageUrl && !imageError ? (
          <img
            src={fallbackImageUrl}
            alt={character.name}
            className="absolute inset-0 w-full h-full object-cover"
            onError={() => setImageError(true)}
            onContextMenu={(e) => e.preventDefault()}
            draggable={false}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center">
            <PhotoIcon className="w-8 h-8 text-gray-300" />
          </div>
        )}

        {/* Top Overlay - Logo & Status */}
        <div className={`absolute top-0 left-0 right-0 pt-2.5 pb-8 px-3 z-10 bg-gradient-to-b ${
          cardTheme === 'dark' ? 'from-black/40 to-transparent' : 'from-white/60 to-transparent'
        }`}>
          <div className="flex items-center justify-between">
            <span className={`text-[9px] font-medium tracking-[0.15em] uppercase ${
              cardTheme === 'dark' ? 'text-white/80' : 'text-black/70'
            }`}>
              ORIFOLI
            </span>
            {/* Public status badge - only for owner */}
            {isOwner && (
              <span className={`inline-flex items-center gap-1 px-2 py-1 text-[12px] font-bold rounded-full ${
                character.isPublic
                  ? 'bg-[#CCFF00] text-black'
                  : 'bg-gray-800/60 text-gray-300 backdrop-blur-sm'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${character.isPublic ? 'bg-black' : 'bg-gray-400'}`} />
                {character.isPublic ? t('status.public') : t('status.private')}
              </span>
            )}
          </div>
        </div>

        {/* English Name - Top Right, Vertical (bottom to top) */}
        {character.nameEnglish && !isSmall && (
          <div
            className="absolute top-4 right-1"
            style={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
            }}
          >
            <span className={`text-[10px] font-light tracking-wider whitespace-nowrap ${
              cardTheme === 'dark' ? 'text-white/80' : 'text-black/80'
            }`}>
              {character.nameEnglish}
            </span>
          </div>
        )}

        {/* Bottom Overlay - Character Info */}
        <div className={`absolute bottom-0 left-0 right-0 pt-16 pb-3 px-3 bg-gradient-to-t ${
          cardTheme === 'dark' ? 'from-black/60 via-black/30 to-transparent' : 'from-white/90 via-white/70 to-transparent'
        }`}>
          {/* Character Name */}
          <p className={`${isSmall ? 'text-base' : 'text-xl'} font-semibold tracking-tight line-clamp-2 ${
            cardTheme === 'dark'
              ? getTextClass('text-white', 'text-white/30')
              : getTextClass('text-black/60', 'text-gray-300')
          }`} style={{ lineHeight: '1.2' }}>
            {character.name}
          </p>

          {/* Name Reading (よみがな) */}
          {character.nameReading && !isSmall && (
            <p className={`text-[9px] mt-0.5 ${
              cardTheme === 'dark'
                ? getTextClass('text-white/80', 'text-white/20')
                : getTextClass('text-black/80', 'text-gray-300')
            }`}>
              {character.nameReading}
            </p>
          )}

          {/* Bio */}
          {character.bio && !isSmall && (
            <p className={`${isSmall ? 'text-[9px]' : 'text-xs'} mt-1 line-clamp-2 leading-relaxed ${
              cardTheme === 'dark'
                ? getTextClass('text-white/60', 'text-white/20')
                : getTextClass('text-gray-600', 'text-gray-300')
            }`}>
              {character.bio}
            </p>
          )}

          {/* Tags Row */}
          {character.tags && character.tags.length > 0 && !isSmall && (
            <div className="flex gap-1.5 mt-1 min-h-[14px]">
              {character.tags.slice(0, 3).map(tag => {
                const translatedTag =
                  tTop(`tags.genre.${tag}`, { defaultValue: '' }) ||
                  tTop(`tags.personality.${tag}`, { defaultValue: '' }) ||
                  tTop(`tags.worldview.${tag}`, { defaultValue: tag });
                return (
                  <span
                    key={tag}
                    className={`text-[10px] ${
                      cardTheme === 'dark'
                        ? getTextClass('text-white/50', 'text-white/15')
                        : getTextClass('text-gray-700', 'text-gray-700')
                    }`}
                  >
                    #{translatedTag}
                  </span>
                );
              })}
            </div>
          )}

          {/* Divider */}
          <div className={`border-t my-2.5 ${
            cardTheme === 'dark'
              ? getTextClass('border-white/20', 'border-white/5')
              : getTextClass('border-gray-300', 'border-gray-200')
          }`} />

          {/* Creator Info */}
          <a
            href={username ? `${config.figryneUrl}/user/${username}` : undefined}
            onClick={(e) => e.stopPropagation()}
            className={`flex items-center gap-2 ${username ? 'hover:opacity-80 transition-opacity cursor-pointer' : ''}`}
          >
            <div className={`${isSmall ? 'w-5 h-5' : 'w-6 h-6'} rounded-full overflow-hidden flex-shrink-0 relative ${
              cardTheme === 'dark' ? 'bg-white/20' : 'bg-gray-200'
            }`}>
              <div className={`absolute inset-0 bg-gradient-to-br ${
                cardTheme === 'dark' ? 'from-white/30 to-white/10' : 'from-gray-300 to-gray-200'
              }`} />
              {cachedAvatarSrc && (
                <img
                  src={cachedAvatarSrc}
                  alt=""
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity ${avatarLoaded ? 'opacity-100' : 'opacity-0'}`}
                  onLoad={() => setAvatarLoaded(true)}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              )}
            </div>
            <p className={`${isSmall ? 'text-[8px]' : 'text-[10px]'} tracking-wide truncate ${
              cardTheme === 'dark'
                ? 'text-white/50'
                : 'text-gray-500'
            }`}>
              <span className={cardTheme === 'dark' ? 'text-white/30' : 'text-gray-400'}>CREATOR/</span>
              {displayName}
              {username && !isSmall && <span className={cardTheme === 'dark' ? 'text-white/30' : 'text-gray-400'}> @{username}</span>}
            </p>
          </a>
        </div>

        {/* Edit button - bottom right, only for owner */}
        {isOwner && onEdit && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="absolute bottom-3 right-3 p-2.5 bg-black hover:bg-gray-800 text-white rounded-full shadow-lg transition-all hover:scale-105"
            title={t('actions.edit')}
          >
            <PencilSolidIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* オンボーディングヒント（overflow-hiddenの外に配置） */}
      {editHint?.show && isOwner && (
        <div
          className="absolute bottom-10 right-[21px] z-[60] pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="absolute bottom-full right-0 mb-1"
          >
            <div className="relative bg-white text-gray-900 text-xs leading-relaxed rounded-xl px-4 py-3 pr-7 shadow-lg w-[200px]">
              <button
                onClick={(e) => { e.stopPropagation(); editHint.onDismiss(); }}
                className="absolute top-1.5 right-1.5 p-0.5 text-gray-400 hover:text-gray-700 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <p>{editHint.message}</p>
            </div>
            {/* 下向き矢印 */}
            <div className="flex justify-end pr-2">
              <svg width="14" height="8" viewBox="0 0 14 8" className="text-white">
                <polygon points="0,0 14,0 7,8" fill="currentColor" />
              </svg>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CharacterPage: React.FC = () => {
  const { characterId } = useParams<{ characterId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation('character');
  const { t: tTop } = useTranslation('top');
  const { t: tFooter } = useTranslation('footer');
  const isMobile = useIsMobile();

  const tabParam = searchParams.get('tab');

  // Character state
  const [character, setCharacter] = useState<Character | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Interaction state
  const [isFollowing, setIsFollowing] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [likeCount, setLikeCount] = useState(0);

  // UI state
  const [activeTab, setActiveTab] = useState<'gallery' | 'settings' | 'fanart'>(() => {
    if (tabParam === 'gallery' || tabParam === 'fanart') return tabParam;
    return 'settings';
  });
  const [settingsSubTab, setSettingsSubTab] = useState<'profile' | 'reference' | 'links'>('profile');
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [showUnfollowConfirm, setShowUnfollowConfirm] = useState(false);

  // Gallery modal state (XOR-Pix)
  const [showGalleryUpload, setShowGalleryUpload] = useState(false);
  const [selectedGalleryImage, setSelectedGalleryImage] = useState<GalleryImage | null>(null);
  const [galleryRefreshKey, setGalleryRefreshKey] = useState(0);

  // Fanart modal state
  const [showFanartUpload, setShowFanartUpload] = useState(false);
  const [selectedFanartImage, setSelectedFanartImage] = useState<FanartImage | null>(null);
  const [fanartRefreshKey, setFanartRefreshKey] = useState(0);

  // Login modal state (for upload/action protection)
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingActionType, setPendingActionType] = useState<'gallery' | 'fanart' | 'adopt' | null>(null);

  // Card viewer modal state
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);

  // 初回作成後のオンボーディングヒント
  const [showEditHint, setShowEditHint] = useState(false);

  // FIGRYNEギフト関連
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftCharacterId, setGiftCharacterId] = useState<string | null>(null);
  const [giftStatus, setGiftStatus] = useState<{ eligible: boolean; claimed: boolean } | null>(null);

  // Links editing state
  const [isEditingLinks, setIsEditingLinks] = useState(false);
  const [editLinks, setEditLinks] = useState<CharacterLink[]>([]);
  const [newLinkPlatform, setNewLinkPlatform] = useState<CharacterLinkPlatform>('youtube');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkLabel, setNewLinkLabel] = useState('');
  const [isSavingLinks, setIsSavingLinks] = useState(false);

  // Header image editing state
  const [showHeaderCropper, setShowHeaderCropper] = useState(false);
  const [tempHeaderImageUrl, setTempHeaderImageUrl] = useState<string | null>(null);
  const [isUploadingHeader, setIsUploadingHeader] = useState(false);
  const [headerImageUrl, setHeaderImageUrl] = useState<string | undefined>(undefined);
  const headerFileInputRef = useRef<HTMLInputElement>(null);

  // Tab refs for auto-scroll
  const tabContainerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  // Fetch character data
  // オーナーは非公開でも閲覧可能にするため、認証付きAPIを先に試す
  const fetchCharacter = useCallback(async () => {
    if (!characterId) return;

    setIsLoading(true);
    setError(null);

    try {
      let data: Character | null = null;

      // ログイン中の場合、まず認証付きAPIを試す（オーナーなら非公開でも取得可能）
      if (user) {
        try {
          data = await getCharacter(characterId);
        } catch {
          // 認証付きAPIが失敗したら公開APIを試す
        }
      }

      // 認証付きAPIで取得できなかった場合、公開APIを試す
      if (!data) {
        data = await getPublicCharacter(characterId);
      }

      if (data) {
        setCharacter(data);
        setFollowerCount(data.stats?.followerCount || 0);
        setLikeCount(data.stats?.likeCount || 0);
      } else {
        setError(t('errors.notFound'));
      }
    } catch (err) {
      console.error('Error fetching character:', err);
      setError(t('errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [characterId, user]);

  // Check follow/like status
  const checkInteractionStatus = useCallback(async () => {
    if (!characterId || !user) return;

    try {
      const [following, liked] = await Promise.all([
        isFollowingCharacter(characterId, user.uid),
        hasLikedCharacter(characterId, user.uid)
      ]);
      setIsFollowing(following);
      setIsLiked(liked);
    } catch (err) {
      console.error('Error checking interaction status:', err);
    }
  }, [characterId, user]);

  // Handle follow
  const handleFollow = async () => {
    if (!characterId || !user || isProcessing) return;

    // フォロー中の場合、確認モーダルを表示
    if (isFollowing) {
      setShowUnfollowConfirm(true);
      return;
    }

    // フォローする
    await executeFollow();
  };

  // Execute follow/unfollow
  const executeFollow = async () => {
    if (!characterId || !user) return;

    setIsProcessing(true);
    try {
      const nowFollowing = await toggleCharacterFollow(characterId, user.uid, isFollowing);
      setIsFollowing(nowFollowing);
      setFollowerCount(prev => nowFollowing ? prev + 1 : prev - 1);
    } catch (err) {
      console.error('Error toggling follow:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle unfollow confirmation
  const handleUnfollowConfirm = async () => {
    setShowUnfollowConfirm(false);
    await executeFollow();
  };

  // Handle like
  const handleLike = async () => {
    if (!characterId || !user || isProcessing) return;

    setIsProcessing(true);
    try {
      const { liked: nowLiked, likeCount: newCount } = await toggleCharacterLike(characterId, user.uid);
      setIsLiked(nowLiked);
      setLikeCount(newCount);
    } catch (err) {
      console.error('Error toggling like:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  // Header image handlers
  const handleHeaderFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setTempHeaderImageUrl(reader.result as string);
        setShowHeaderCropper(true);
      };
      reader.readAsDataURL(file);
    }
    // Reset input
    if (headerFileInputRef.current) {
      headerFileInputRef.current.value = '';
    }
  };

  const handleHeaderCropSave = async (croppedFile: File) => {
    if (!characterId) return;

    try {
      setIsUploadingHeader(true);
      const newHeaderUrl = await updateCharacterHeaderImage(characterId, croppedFile);
      setHeaderImageUrl(newHeaderUrl);
      if (character) {
        setCharacter({ ...character, headerImageUrl: newHeaderUrl });
      }
      setShowHeaderCropper(false);
      setTempHeaderImageUrl(null);
    } catch (error) {
      console.error('ヘッダー画像アップロードエラー:', error);
      throw error;
    } finally {
      setIsUploadingHeader(false);
    }
  };

  const handleHeaderCropCancel = () => {
    setShowHeaderCropper(false);
    setTempHeaderImageUrl(null);
  };

  // Links editing handlers
  const handleStartEditLinks = () => {
    setEditLinks(character?.links || []);
    setNewLinkPlatform('youtube');
    setNewLinkUrl('');
    setNewLinkLabel('');
    setIsEditingLinks(true);
  };

  const handleCancelEditLinks = () => {
    setIsEditingLinks(false);
  };

  const handleAddLink = () => {
    if (editLinks.length >= MAX_CHARACTER_LINKS) return;
    const sanitizedUrl = sanitizeInput(newLinkUrl.trim(), { urlOnly: true, maxLength: 500 });
    if (!sanitizedUrl) return;

    const newLink: CharacterLink = {
      platform: newLinkPlatform,
      url: sanitizedUrl,
    };
    if (newLinkPlatform === 'other' && newLinkLabel.trim()) {
      newLink.label = sanitizeInput(newLinkLabel.trim(), { stripHtml: true, stripScripts: true, maxLength: 30 });
    }

    setEditLinks(prev => [...prev, newLink]);
    setNewLinkUrl('');
    setNewLinkLabel('');
  };

  const handleRemoveLink = (index: number) => {
    setEditLinks(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveLinks = async () => {
    if (!character || isSavingLinks) return;
    setIsSavingLinks(true);
    try {
      const linksToSave = editLinks.length > 0 ? editLinks : undefined;
      await updateCharacterData(character.id, { links: linksToSave } as Partial<Character>);
      setCharacter(prev => prev ? { ...prev, links: linksToSave ? editLinks : undefined } : prev);
      setIsEditingLinks(false);
    } catch (err) {
      console.error('Failed to save links:', err);
    } finally {
      setIsSavingLinks(false);
    }
  };

  // Handle tab change
  const handleTabChange = (tab: 'gallery' | 'settings' | 'fanart') => {
    setActiveTab(tab);
    const newParams = new URLSearchParams(searchParams);
    if (tab === 'gallery') {
      newParams.delete('tab');
    } else {
      newParams.set('tab', tab);
    }
    setSearchParams(newParams, { replace: true });

    // Scroll to active tab
    const tabElement = tabRefs.current.get(tab);
    if (tabElement && tabContainerRef.current) {
      tabElement.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  };

  // Initialize
  useEffect(() => {
    fetchCharacter();
  }, [fetchCharacter]);

  useEffect(() => {
    checkInteractionStatus();
  }, [checkInteractionStatus]);

  // pendingToast チェック（キャラ登録完了時など）
  useEffect(() => {
    const message = sessionStorage.getItem('pendingToast');
    if (message) {
      sessionStorage.removeItem('pendingToast');
      setTimeout(() => showToast(message), 600);
    }
    // 初回作成後のオンボーディングヒント（鉛筆マーク）
    const hint = sessionStorage.getItem('showCardEditHint');
    if (hint) {
      sessionStorage.removeItem('showCardEditHint');
      setTimeout(() => setShowEditHint(true), 1200);
    }

    // FIGRYNE作成完了からの戻りフロー
    if (searchParams.get('figryneCreated') === 'true') {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('figryneCreated');
      setSearchParams(newParams, { replace: true });
      setTimeout(() => showToast(t('gift.returnSuccess', 'FIGRYNEが完成しました！')), 600);
    }
  }, []);

  // VTuberキャラクターのオーナーの場合、FIGRYNEギフト状態をチェック
  useEffect(() => {
    if (!character || !user || user.uid !== character.ownerId || character.category !== 'vtuber') return;

    const checkGiftStatus = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const gift = userDoc.data()?.vtuberFigryneGift;
          if (gift?.eligible) {
            setGiftStatus({ eligible: true, claimed: gift.claimed || false });
          }
        }
      } catch (err) {
        console.error('Error checking gift status:', err);
      }
    };

    checkGiftStatus();
  }, [character, user]);

  // Loading state - Match initial loader style for seamless transition
  if (isLoading) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center select-none"
        style={{ background: 'linear-gradient(135deg, #4a4458 0%, #3d3a4d 50%, #2e2b3a 100%)' }}
        onContextMenu={(e) => e.preventDefault()}
        onCopy={(e) => e.preventDefault()}
      >
        <Toast />
        <img
          src={logoWhite}
          alt="ORIFOLI"
          className="w-48 md:w-52 mb-6 opacity-90"
        />
        <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  // Error state
  if (error || !character) {
    return (
      <div
        className="min-h-screen bg-[#f8f7f8] dark:bg-gray-900 flex items-center justify-center select-none"
        onContextMenu={(e) => e.preventDefault()}
        onCopy={(e) => e.preventDefault()}
      >
        <Toast />
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400 mb-4">{error || t('errors.notFound')}</p>
          <Link to="/" className="text-gray-900 dark:text-white hover:underline">
            {t('navigation.backToTop')}
          </Link>
        </div>
      </div>
    );
  }

  const isOwner = user?.uid === character.ownerId;

  // Profile section renderer
  const renderProfileSection = (isMobile: boolean) => (
    <div className={isMobile ? "px-4 pb-3" : "px-6 pb-4 ml-1 mr-1"}>
      {/* Character Card - larger on mobile, dimmed text on desktop */}
      <div
        className={`relative mb-4 cursor-pointer ${isMobile ? "flex justify-center" : "-mt-[140px]"}`}
        onClick={() => setIsCardModalOpen(true)}
      >
        <CharacterCard
          character={character}
          size="large"
          dimText={!isMobile}
          isOwner={isOwner}
          onEdit={() => setIsEditModalOpen(true)}
          editHint={showEditHint ? {
            show: true,
            message: t('hints.editPublish', 'ここから公開設定やファンアート許可ができます'),
            onDismiss: () => {
              setShowEditHint(false);
              // 編集ヒント閉じ後、FIGRYNEギフトモーダルを遅延表示
              setTimeout(() => {
                const giftId = sessionStorage.getItem('showFigryneGiftModal');
                if (giftId) {
                  sessionStorage.removeItem('showFigryneGiftModal');
                  setGiftCharacterId(giftId);
                  setShowGiftModal(true);
                }
              }, 600);
            },
          } : undefined}
        />
      </div>

      {/* FIGRYNEギフトバナー（eligible=true & claimed=false の場合のみ） */}
      {isOwner && giftStatus?.eligible && !giftStatus?.claimed && (
        <div className="mb-4 mx-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3">
          <div className="flex items-center gap-2.5 mb-2">
            <GiftIcon className="w-4 h-4 text-gray-700 dark:text-gray-300 flex-shrink-0" />
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              {t('gift.bannerTitle', 'FIGRYNEギフト未使用')}
            </p>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            {t('gift.bannerDescription', 'プレミアムマテリアル1体無料')}
          </p>
          <button
            onClick={() => {
              const returnUrl = encodeURIComponent(window.location.href);
              window.location.href = `${config.figryneUrl}/creator/gift-upload?characterId=${characterId}&returnUrl=${returnUrl}`;
            }}
            className="w-full py-2 bg-gray-900 dark:bg-gray-100 hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-gray-900 text-sm font-semibold rounded-lg transition-colors"
          >
            {t('gift.bannerButton', 'FIGRYNEを作る')}
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 mb-5 text-sm mt-0">
        <div className="flex items-center justify-center gap-1.5 text-gray-600 dark:text-gray-400">
          <UserIcon className="w-4 h-4" />
          <span className="font-bold text-gray-900 dark:text-white">{followerCount}</span>
        </div>
        <div className="flex items-center justify-center gap-1.5 text-gray-600 dark:text-gray-400">
          <HeartIcon className="w-4 h-4" />
          <span className="font-bold text-gray-900 dark:text-white">{likeCount}</span>
        </div>
        <div className="flex items-center justify-center gap-1.5 text-gray-600 dark:text-gray-400">
          <PaintBrushIcon className="w-4 h-4" />
          <span className="font-bold text-gray-900 dark:text-white">{character.stats?.fanartCount || 0}</span>
        </div>
      </div>

      {/* Desktop only: Name, Creator, Bio, Tags (card shows preview, here we show full) */}
      {!isMobile && (
        <>
          {/* Name */}
          <div className="mb-1 ml-1">
            <div className="flex items-center gap-2">
              <h1 className="text-xl md:text-[1.75rem] font-extrabold text-gray-900 dark:text-white leading-tight tracking-tight mb-0.5 break-words">
                {character.name}
              </h1>
              {character.xVerified && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black text-white text-[10px] font-medium flex-shrink-0">
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  {character.xUsername && `@${character.xUsername}`}
                </span>
              )}
            </div>
            {character.nameReading && (
              <p className="text-xs text-gray-500 dark:text-gray-400">{character.nameReading}</p>
            )}
          </div>

          {/* Bio - expanded version for desktop */}
          {character.bio && (
            <div className="mb-3 mt-3 ml-1">
              <p className="text-xs md:text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]">
                {isBioExpanded || character.bio.split('\n').length <= 2
                  ? character.bio
                  : character.bio.split('\n').slice(0, 2).join('\n') + '...'}
              </p>
              {character.bio.split('\n').length > 2 && (
                <button
                  onClick={() => setIsBioExpanded(!isBioExpanded)}
                  className="text-purple-600 dark:text-purple-400 text-xs font-medium hover:text-purple-700 dark:hover:text-purple-300 mt-1"
                >
                  {isBioExpanded ? t('bio.collapse') : t('bio.expand')}
                </button>
              )}
            </div>
          )}

          {/* Tags */}
          {character.tags && character.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4 ml-1">
              {character.tags.map((tag, index) => {
                const translatedTag =
                  tTop(`tags.genre.${tag}`, { defaultValue: '' }) ||
                  tTop(`tags.personality.${tag}`, { defaultValue: '' }) ||
                  tTop(`tags.worldview.${tag}`, { defaultValue: tag });
                return (
                  <span
                    key={index}
                    className="px-2.5 py-1 bg-white dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 text-xs rounded-full"
                  >
                    #{translatedTag}
                  </span>
                );
              })}
            </div>
          )}

          {/* Link Icons */}
          {character.links && character.links.length > 0 && (
            <div className="flex flex-wrap gap-3 mb-4 ml-1">
              {character.links.map((link, index) => {
                const platformInfo = CHARACTER_LINK_PLATFORMS[link.platform];
                const IconComponent = platformInfo.icon;
                const displayName = link.platform === 'other' && link.label ? link.label : platformInfo.name;
                return (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-500 dark:text-gray-400 hover:opacity-70 transition-opacity"
                    title={displayName}
                  >
                    <IconComponent className="w-5 h-5" style={{ color: platformInfo.color }} />
                  </a>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Action Buttons */}
      <div className={`flex flex-col gap-2 ${isMobile ? "items-center" : ""}`}>
        {!isOwner && user && (
          <div className={`flex gap-2 ${isMobile ? "" : "flex-col w-full"}`}>
            <button
              onClick={handleFollow}
              disabled={isProcessing}
              className={`py-2 px-6 rounded-full font-semibold text-sm transition-all ${
                isFollowing
                  ? 'bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
                  : 'bg-purple-500 text-white hover:bg-purple-600'
              } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''} ${!isMobile ? 'w-full' : ''}`}
            >
              {isFollowing ? t('actions.following') : t('actions.follow')}
            </button>
            <button
              onClick={handleLike}
              disabled={isProcessing}
              className={`py-2 px-6 rounded-full font-semibold text-sm transition-all flex items-center justify-center gap-1 ${
                isLiked
                  ? 'bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-500'
              } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''} ${!isMobile ? 'w-full' : ''}`}
            >
              {isLiked ? (
                <HeartSolidIcon className="w-4 h-4 text-red-500" />
              ) : (
                <HeartIcon className="w-4 h-4" />
              )}
              {isLiked ? t('actions.liked') : t('actions.like')}
            </button>
          </div>
        )}
      </div>

      {/* おむかえ販売バナー（販売中の場合） */}
      {character.forSale && character.salePrice && !isOwner && (
        <div className={`mt-3 mb-3 p-3 bg-gray-100 dark:bg-gray-800 border border-purple-500/20 rounded-xl ${isMobile ? "text-center" : ""}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-xs text-black dark:text-purple-400 font-medium mb-1">{t('adoption.available')}</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                ¥{character.salePrice.toLocaleString()}
              </p>
            </div>
            <button
              onClick={() => {
                if (!user) {
                  setPendingActionType('adopt');
                  setShowLoginModal(true);
                } else {
                  navigate(`/adopt/${character.id}`);
                }
              }}
              className="px-5 py-2 bg-[#CCFF00] text-black text-sm font-medium rounded-full hover:from-purple-600 hover:to-pink-600 transition-all shadow-lg"
            >
              {t('adoption.button')}
            </button>
          </div>
        </div>
      )}


      {/* AR Viewer Section - Show if character has AR model */}
      {character.arModelId && character.arModelStatus === 'active' && (
        <div className={`mt-4 ${isMobile ? "" : ""}`}>
          <ARViewerSection
            arModelId={character.arModelId}
            characterName={character.name}
            cardTheme={character.cardTheme || 'dark'}
          />
        </div>
      )}
    </div>
  );

  // Tab options
  const tabOptions = [
    { id: 'settings' as const, label: t('tabs.settings') },
    { id: 'gallery' as const, label: t('tabs.gallery') },
    ...(character.allowFanart ? [{ id: 'fanart' as const, label: t('tabs.fanart') }] : []),
  ];

  return (
    <div
      className="min-h-screen flex flex-col bg-[#f8f7f8] dark:bg-gray-900 select-none"
      onContextMenu={(e) => e.preventDefault()}
      onCopy={(e) => e.preventDefault()}
    >
      <Toast />
      {/* Fixed Navigation - With gradient overlay for readability */}
      <header className="fixed top-0 left-0 right-0 z-40">
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/15 to-transparent pointer-events-none" />
        <div className="relative h-14 md:h-16 flex items-center justify-between px-3 md:px-6">
          {/* Left: Back button + Name + Status Badge + Share */}
          <div className="flex items-center gap-1">
            <button onClick={() => navigate('/')} className="text-white hover:opacity-70 transition-opacity">
              <ChevronLeftIcon className="w-7 h-7" />
            </button>
            <span className="text-white font-semibold text-base md:text-lg truncate max-w-[200px]">
              {character.name}
            </span>
            {/* 非公開バッジ - オーナーのみに表示 */}
            {isOwner && !character.isPublic && (
              <span className="ml-1 px-2 py-0.5 bg-gray-800/70 text-gray-300 text-[10px] font-medium rounded-full backdrop-blur-sm">
                {t('status.private')}
              </span>
            )}
            <button
              onClick={() => setIsShareModalOpen(true)}
              className="p-1.5 text-white hover:opacity-70 transition-opacity"
              aria-label={t('actions.share')}
            >
              <ShareIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Right: Theme Toggle + User Avatar */}
          <div className="flex items-center gap-2">
            <ThemeToggle className="text-white hover:text-white/80 hover:bg-white/20" />
            {user ? (
              <Link to="/my-characters" className="block">
                <div className="w-9 h-9 rounded-full overflow-hidden border-2 border-white/30 shadow-lg">
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-sm font-bold">
                      {user.displayName?.charAt(0) || user.email?.charAt(0) || '?'}
                    </div>
                  )}
                </div>
              </Link>
            ) : (
              <Link
                to="/login"
                className="px-4 py-1.5 bg-[#B566FF] backdrop-blur-sm text-white text-xs font-medium rounded-full hover:bg-white/30 transition-colors"
              >
                {t('navigation.login')}
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Header Image */}
      <div className="w-full aspect-[3/1] md:aspect-[21/9] max-h-[320px] bg-gradient-to-br from-purple-400 via-pink-300 to-orange-300 relative overflow-hidden group">
        {/* Hidden file input */}
        <input
          type="file"
          ref={headerFileInputRef}
          onChange={handleHeaderFileSelect}
          accept="image/*"
          className="hidden"
          disabled={isUploadingHeader}
        />

        {(headerImageUrl || character.headerImageUrl) ? (
          <img
            src={headerImageUrl || character.headerImageUrl}
            alt=""
            className="w-full h-full object-cover"
            onContextMenu={(e) => e.preventDefault()}
            draggable={false}
          />
        ) : (character.thumbnailUrl || character.avatarUrl) ? (
          // Use thumbnail/avatar as blurred background if no header
          <div className="w-full h-full relative">
            <img
              src={character.thumbnailUrl || character.avatarUrl}
              alt=""
              className="w-full h-full object-cover scale-150 blur-2xl opacity-60"
              onContextMenu={(e) => e.preventDefault()}
              draggable={false}
            />
          </div>
        ) : null}

        {/* Edit button - only visible to owner */}
        {isOwner && (
          <button
            type="button"
            onClick={() => headerFileInputRef.current?.click()}
            disabled={isUploadingHeader}
            className="absolute bottom-3 right-3 z-10 p-3 bg-black/40 hover:bg-black/70 text-white rounded-full transition-all hover:scale-105 disabled:opacity-50 cursor-pointer"
            title={t('actions.changeHeader')}
          >
            {isUploadingHeader ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <CameraIcon className="w-5 h-5" />
            )}
          </button>
        )}
      </div>

      {/* Header Image Cropper */}
      {showHeaderCropper && tempHeaderImageUrl && (
        <HeaderImageCropper
          imageUrl={tempHeaderImageUrl}
          isOpen={showHeaderCropper}
          onCancel={handleHeaderCropCancel}
          onSave={handleHeaderCropSave}
          isUploading={isUploadingHeader}
        />
      )}

      {/* Character Edit Modal */}
      <CharacterEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        characterId={characterId || null}
        onSaved={() => {
          // Refresh character data after save
          fetchCharacter();
        }}
      />

      {/* Character Share Modal */}
      <CharacterShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        characterId={characterId || ''}
        characterName={character.name}
      />

      {/* Character Settings Modal */}
      <CharacterSettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        character={character}
        onSaved={(updates) => {
          // Update local character state with saved values
          setCharacter(prev => prev ? { ...prev, ...updates } : prev);
        }}
      />

      {/* Unfollow Confirmation Modal */}
      {showUnfollowConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setShowUnfollowConfirm(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-sm w-full shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              {t('unfollow.title')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              {t('unfollow.message', { name: character.name })}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowUnfollowConfirm(false)}
                className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {t('unfollow.cancel')}
              </button>
              <button
                onClick={handleUnfollowConfirm}
                className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {t('unfollow.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content Container */}
      <div className="flex-1 max-w-[1400px] mx-auto pb-20 w-full">
        {/* Mobile: Profile Section - JSで条件分岐（XorPixViewerの重複レンダリング防止） */}
        {isMobile && (
          <div className="-mt-10">
            {renderProfileSection(true)}
          </div>
        )}

        {/* Main Layout */}
        <div className="md:flex">
          {/* Desktop: Sidebar - JSで条件分岐（XorPixViewerの重複レンダリング防止） */}
          {!isMobile && (
            <aside className="w-[280px] lg:w-[320px] shrink-0">
              <div className="sticky top-[120px]">
                {renderProfileSection(false)}
              </div>
            </aside>
          )}

          {/* Main Content Area */}
          <main className="flex-1 min-w-0">
            {/* Tabs */}
            <div className="sticky top-0 z-30 bg-[#f8f7f8] dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4">
              <div
                ref={tabContainerRef}
                className="flex overflow-x-auto scrollbar-hide scroll-smooth snap-x snap-mandatory"
              >
                {tabOptions.map((tab) => (
                  <button
                    key={tab.id}
                    ref={(el) => {
                      if (el) tabRefs.current.set(tab.id, el);
                    }}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex-shrink-0 px-5 py-3 md:py-5 text-base font-bold transition-colors relative snap-start ${
                      activeTab === tab.id
                        ? 'text-gray-900 dark:text-white'
                        : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                    }`}
                  >
                    {tab.label}
                    {activeTab === tab.id && (
                      <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#CCFF00]" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="py-3.5 px-3.5 md:p-4">
              {activeTab === 'gallery' && (
                <CharacterGalleryGrid
                  key={galleryRefreshKey}
                  characterId={characterId || ''}
                  characterName={character?.name}
                  isOwner={isOwner}
                  onImageClick={(img) => setSelectedGalleryImage(img)}
                  onUploadClick={() => {
                    if (!user) {
                      setPendingActionType('gallery');
                      setShowLoginModal(true);
                    } else {
                      setShowGalleryUpload(true);
                    }
                  }}
                />
              )}

              {activeTab === 'settings' && (
                <div className="space-y-4">
                  {/* サブタブ切り替え */}
                  <div className="flex items-center gap-2 mb-4 overflow-x-auto scrollbar-hide">
                    <button
                      onClick={() => setSettingsSubTab('profile')}
                      className={`px-4 py-2 rounded-full text-sm font-bold transition-colors whitespace-nowrap flex-shrink-0 ${
                        settingsSubTab === 'profile'
                          ? 'bg-[#495674] text-white'
                          : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {t('settings.subTabs.profile')}
                    </button>
                    <button
                      onClick={() => { setSettingsSubTab('links'); setIsEditingLinks(false); }}
                      className={`px-4 py-2 rounded-full text-sm font-bold transition-colors whitespace-nowrap flex-shrink-0 ${
                        settingsSubTab === 'links'
                          ? 'bg-[#495674] text-white'
                          : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {t('settings.subTabs.links', 'リンク集')}
                    </button>
                    <button
                      onClick={() => setSettingsSubTab('reference')}
                      className={`px-4 py-2 rounded-full text-sm font-bold transition-colors whitespace-nowrap flex-shrink-0 ${
                        settingsSubTab === 'reference'
                          ? 'bg-[#495674] text-white'
                          : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {t('settings.subTabs.reference')}
                    </button>
                  </div>

                  {/* プロフィールサブタブ */}
                  {settingsSubTab === 'profile' && (
                    <>
                      {/* Edit button for owner */}
                      {isOwner && (
                        <div className="flex justify-end">
                          <button
                            onClick={() => setIsSettingsModalOpen(true)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition-colors"
                          >
                            <PencilIcon className="w-4 h-4" />
                            {t('settings.edit', '編集')}
                          </button>
                        </div>
                      )}
                      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 md:p-6 space-y-4">
                        {character.species && (
                          <div className="flex py-2 border-b border-gray-100 dark:border-gray-700">
                            <span className="text-gray-500 dark:text-gray-400 text-sm w-24 flex-shrink-0">{t('settings.species')}</span>
                            <span className="text-gray-900 dark:text-white font-medium text-sm">{character.species}</span>
                          </div>
                        )}
                        {character.gender && (
                          <div className="flex py-2 border-b border-gray-100 dark:border-gray-700">
                            <span className="text-gray-500 dark:text-gray-400 text-sm w-24 flex-shrink-0">{t('settings.gender')}</span>
                            <span className="text-gray-900 dark:text-white font-medium text-sm">{character.gender}</span>
                          </div>
                        )}
                        {character.age && (
                          <div className="flex py-2 border-b border-gray-100 dark:border-gray-700">
                            <span className="text-gray-500 dark:text-gray-400 text-sm w-24 flex-shrink-0">{t('settings.age')}</span>
                            <span className="text-gray-900 dark:text-white font-medium text-sm">{character.age}</span>
                          </div>
                        )}
                        {character.birthday && (
                          <div className="flex py-2 border-b border-gray-100 dark:border-gray-700">
                            <span className="text-gray-500 dark:text-gray-400 text-sm w-24 flex-shrink-0">{t('settings.birthday')}</span>
                            <span className="text-gray-900 dark:text-white font-medium text-sm">{character.birthday}</span>
                          </div>
                        )}
                        {character.height && (
                          <div className="flex py-2 border-b border-gray-100 dark:border-gray-700">
                            <span className="text-gray-500 dark:text-gray-400 text-sm w-24 flex-shrink-0">{t('settings.height')}</span>
                            <span className="text-gray-900 dark:text-white font-medium text-sm">{character.height}</span>
                          </div>
                        )}
                        {character.personality && character.personality.length > 0 && (
                          <div className="flex py-2 border-b border-gray-100 dark:border-gray-700">
                            <span className="text-gray-500 dark:text-gray-400 text-sm w-24 flex-shrink-0 pt-1">{t('settings.personality')}</span>
                            <div className="flex flex-wrap gap-2">
                              {character.personality.map((trait, i) => (
                                <span key={i} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-full">
                                  {trait}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {character.likes && character.likes.length > 0 && (
                          <div className="flex py-2 border-b border-gray-100 dark:border-gray-700">
                            <span className="text-gray-500 dark:text-gray-400 text-sm w-24 flex-shrink-0 pt-1">{t('settings.likes')}</span>
                            <div className="flex flex-wrap gap-2">
                              {character.likes.map((item, i) => (
                                <span key={i} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-full">
                                  {item}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {character.dislikes && character.dislikes.length > 0 && (
                          <div className="flex py-2">
                            <span className="text-gray-500 dark:text-gray-400 text-sm w-24 flex-shrink-0 pt-1">{t('settings.dislikes')}</span>
                            <div className="flex flex-wrap gap-2">
                              {character.dislikes.map((item, i) => (
                                <span key={i} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-full">
                                  {item}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {!character.species && !character.gender && !character.age && !character.birthday && !character.height && (!character.personality || character.personality.length === 0) && (!character.likes || character.likes.length === 0) && (!character.dislikes || character.dislikes.length === 0) && (
                          isOwner ? (
                            <div className="text-center py-4">
                              <LightBulbIcon className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                              <p className="text-gray-700 dark:text-gray-300 text-sm mb-4">
                                {t('settings.emptyOwnerHint', { name: character.name })}
                              </p>
                              <button
                                onClick={() => setIsSettingsModalOpen(true)}
                                className="inline-flex items-center gap-2 px-5 py-2 bg-purple-500 text-white rounded-full text-sm font-medium hover:bg-purple-600 transition-colors"
                              >
                                <PencilIcon className="w-4 h-4" />
                                {t('settings.edit')}
                              </button>
                            </div>
                          ) : (
                            <p className="text-gray-400 dark:text-gray-500 text-sm text-center py-4">{t('settings.empty')}</p>
                          )
                        )}
                      </div>
                    </>
                  )}

                  {/* 設定資料サブタブ */}
                  {settingsSubTab === 'reference' && (
                    <ReferenceGallery
                      characterId={characterId || ''}
                      characterName={character?.name}
                      isOwner={isOwner}
                    />
                  )}

                  {/* リンク集サブタブ */}
                  {settingsSubTab === 'links' && (
                    <>
                      {/* Edit button for owner */}
                      {isOwner && !isEditingLinks && (
                        <div className="flex justify-end">
                          <button
                            onClick={handleStartEditLinks}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition-colors"
                          >
                            <PencilIcon className="w-4 h-4" />
                            {t('settings.edit', '編集')}
                          </button>
                        </div>
                      )}

                      {isEditingLinks ? (
                        /* 編集モード */
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 md:p-6">
                          <div className="flex items-center gap-2 mb-4">
                            <LinkIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings.links', 'リンク集')}</h3>
                            <span className={`text-xs ml-auto ${editLinks.length >= MAX_CHARACTER_LINKS ? 'text-red-500' : 'text-gray-400'}`}>
                              {editLinks.length}/{MAX_CHARACTER_LINKS}
                            </span>
                          </div>

                          {/* Existing Links */}
                          {editLinks.length > 0 && (
                            <div className="space-y-1 mb-4">
                              {editLinks.map((link, index) => {
                                const platformInfo = CHARACTER_LINK_PLATFORMS[link.platform];
                                const displayName = link.platform === 'other' && link.label ? link.label : platformInfo.name;
                                const IconComponent = platformInfo.icon;
                                return (
                                  <div key={index} className="flex items-center gap-3 bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2">
                                    <IconComponent className="w-5 h-5 flex-shrink-0" style={{ color: platformInfo.color }} />
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200 flex-1">
                                      {displayName}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveLink(index)}
                                      className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Add Link Form */}
                          {editLinks.length < MAX_CHARACTER_LINKS && (
                            <div className="space-y-2 mb-4">
                              <div className="flex gap-2">
                                <select
                                  value={newLinkPlatform}
                                  onChange={(e) => setNewLinkPlatform(e.target.value as CharacterLinkPlatform)}
                                  className="w-32 flex-shrink-0 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                >
                                  {Object.entries(CHARACTER_LINK_PLATFORMS).map(([key, info]) => (
                                    <option key={key} value={key}>{info.name}</option>
                                  ))}
                                </select>
                                <input
                                  type="url"
                                  value={newLinkUrl}
                                  onChange={(e) => setNewLinkUrl(e.target.value)}
                                  placeholder="https://..."
                                  className="flex-1 min-w-0 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  maxLength={500}
                                />
                                <button
                                  type="button"
                                  onClick={handleAddLink}
                                  disabled={!newLinkUrl.trim()}
                                  className="px-3 py-2 bg-purple-500 hover:bg-purple-600 disabled:bg-gray-200 dark:disabled:bg-gray-700 text-white disabled:text-gray-400 rounded-lg transition-colors"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                </button>
                              </div>
                              {newLinkPlatform === 'other' && (
                                <input
                                  type="text"
                                  value={newLinkLabel}
                                  onChange={(e) => setNewLinkLabel(e.target.value)}
                                  placeholder={t('settings.linksLabelPlaceholder', 'リンク名（例: Pixiv）')}
                                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  maxLength={30}
                                />
                              )}
                            </div>
                          )}

                          {/* Save / Cancel buttons */}
                          <div className="flex gap-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                            <button
                              type="button"
                              onClick={handleCancelEditLinks}
                              className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            >
                              {t('common.cancel', 'キャンセル')}
                            </button>
                            <button
                              type="button"
                              onClick={handleSaveLinks}
                              disabled={isSavingLinks}
                              className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white text-sm font-medium rounded-lg transition-colors"
                            >
                              {isSavingLinks ? t('common.saving', '保存中...') : t('common.save', '保存')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* 表示モード */
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 md:p-6">
                          {character.links && character.links.length > 0 ? (
                            <div className="space-y-1">
                              {character.links.map((link, index) => {
                                const platformInfo = CHARACTER_LINK_PLATFORMS[link.platform];
                                const displayName = link.platform === 'other' && link.label ? link.label : platformInfo.name;
                                const IconComponent = platformInfo.icon;
                                return (
                                  <a
                                    key={index}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
                                  >
                                    <IconComponent className="w-5 h-5 flex-shrink-0" style={{ color: platformInfo.color }} />
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                                      {displayName}
                                    </span>
                                  </a>
                                );
                              })}
                            </div>
                          ) : (
                            isOwner ? (
                              <div className="text-center py-6">
                                <LinkIcon className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                                <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                                  {t('settings.linksEmptyOwner', 'リンクを追加して活動先を共有しましょう')}
                                </p>
                                <button
                                  onClick={handleStartEditLinks}
                                  className="inline-flex items-center gap-2 px-5 py-2 bg-purple-500 text-white rounded-full text-sm font-medium hover:bg-purple-600 transition-colors"
                                >
                                  <PencilIcon className="w-4 h-4" />
                                  {t('settings.edit', '編集')}
                                </button>
                              </div>
                            ) : (
                              <p className="text-gray-400 dark:text-gray-500 text-sm text-center py-6">
                                {t('settings.linksEmpty', 'リンクはまだ登録されていません')}
                              </p>
                            )
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {activeTab === 'fanart' && character.allowFanart && (
                <>
                  {/* Fanart accepting message */}
                  <div className="flex items-center gap-3 mb-4 p-3 bg-white dark:bg-gray-800 rounded-xl">
                    {/* Creator avatar + name - clickable to Figryne profile */}
                    <a
                      href={character.creatorUsername ? `${config.figryneUrl}/user/${character.creatorUsername}` : undefined}
                      className={`flex items-center gap-3 ${character.creatorUsername ? 'hover:opacity-80 transition-opacity cursor-pointer' : ''}`}
                    >
                      <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-gray-200 dark:bg-gray-700">
                        {character.creatorAvatar ? (
                          <img
                            src={character.creatorAvatar}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500 text-xs font-bold">
                            {(character.creatorName || character.creatorUsername || 'C').charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      {/* Message */}
                      <p className="text-xs text-gray-600 dark:text-gray-300">
                        <span className="text-gray-400 dark:text-gray-500">CREATOR/</span>
                        {t('fanart.acceptingMessage', {
                          creatorName: character.creatorName || character.creatorUsername || 'Creator',
                          characterName: character.name
                        })}
                      </p>
                    </a>
                  </div>

                  <CharacterFanartGrid
                    key={fanartRefreshKey}
                    characterId={characterId || ''}
                    isOwner={isOwner}
                    onImageClick={(img) => setSelectedFanartImage(img as FanartImage)}
                    onUploadClick={() => {
                      if (!user) {
                        setPendingActionType('fanart');
                        setShowLoginModal(true);
                      } else {
                        setShowFanartUpload(true);
                      }
                    }}
                  />

                  {/* Fanart Guidelines */}
                  {character.fanartGuidelines && (
                    <div className="mt-8 p-4 bg-white dark:bg-gray-800 rounded-xl">
                      <h3 className="font-medium text-gray-900 dark:text-white mb-2 text-sm">{t('fanart.guidelines')}</h3>
                      <p className="text-xs text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{character.fanartGuidelines}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Gallery Upload Modal */}
      {showGalleryUpload && (
        <CharacterGalleryUpload
          characterId={characterId || ''}
          onUploadComplete={() => {
            setShowGalleryUpload(false);
            // Clear cache and refresh gallery by changing key
            if (characterId) {
              orifoliCache.clearFeedCache('gallery', characterId);
            }
            setGalleryRefreshKey(prev => prev + 1);
          }}
          onClose={() => setShowGalleryUpload(false)}
        />
      )}

      {/* Gallery Photo Modal */}
      <PhotoModal
        isOpen={!!selectedGalleryImage}
        onClose={() => setSelectedGalleryImage(null)}
        image={selectedGalleryImage}
      />

      {/* Fanart Upload Modal */}
      {showFanartUpload && (
        <FanartUploadModal
          characterId={characterId || ''}
          onUploadComplete={() => {
            setShowFanartUpload(false);
            // Clear cache and refresh fanart by changing key
            if (characterId) {
              orifoliCache.clearFeedCache('fanart', characterId);
            }
            setFanartRefreshKey(prev => prev + 1);
          }}
          onClose={() => setShowFanartUpload(false)}
        />
      )}

      {/* Fanart Photo Modal */}
      <PhotoModal
        isOpen={!!selectedFanartImage}
        onClose={() => setSelectedFanartImage(null)}
        image={selectedFanartImage}
      />

      {/* Card Viewer Modal (3D card) */}
      <CardViewerModal
        character={character ? {
          id: character.id,
          name: character.name,
          nameReading: character.nameReading,
          nameEnglish: character.nameEnglish,
          bio: character.bio,
          noise1Url: character.noise1Url,
          noise2Url: character.noise2Url,
          rid: character.rid,
          imageWidth: character.imageWidth,
          imageHeight: character.imageHeight,
          thumbnailUrl: character.thumbnailUrl,
          fallbackNoise1Url: character.fallbackNoise1Url,
          fallbackNoise2Url: character.fallbackNoise2Url,
          avatarUrl: character.avatarUrl,
          headerImageUrl: character.headerImageUrl,
          tags: character.tags,
          cardTheme: character.cardTheme,
          ownerId: character.ownerId,
          ownerName: character.ownerName,
          ownerUsername: character.ownerUsername,
          ownerAvatar: character.ownerAvatar,
        } : null}
        isOpen={isCardModalOpen}
        onClose={() => setIsCardModalOpen(false)}
      />

      {/* Login Modal (for upload/action protection) */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => {
          setShowLoginModal(false);
          setPendingActionType(null);
        }}
        onSuccess={() => {
          setShowLoginModal(false);
          // Execute the pending action after successful login
          if (pendingActionType === 'gallery') {
            setShowGalleryUpload(true);
          } else if (pendingActionType === 'fanart') {
            setShowFanartUpload(true);
          } else if (pendingActionType === 'adopt' && character) {
            navigate(`/adopt/${character.id}`);
          }
          setPendingActionType(null);
        }}
      />

      {/* FIGRYNEギフトモーダル */}
      {showGiftModal && giftCharacterId && (
        <FigryneGiftModal
          characterId={giftCharacterId}
          onClose={() => setShowGiftModal(false)}
        />
      )}

      {/* Footer */}
      <footer className="py-6 text-center text-gray-400 dark:text-gray-500 text-xs">
        {tFooter('copyright', { year: new Date().getFullYear() })}
      </footer>
    </div>
  );
};

export default CharacterPage;
